import { useState } from 'react';

import type { BoardDetailOut, FaqType } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, formatDateTime, useAsync } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * 자주하는 질문 — 레거시 board/faq 파리티.
 *
 * 분류(질문 형식)는 레거시 getFaqType(helpers.php:385) 고정 목록 그대로.
 * 레거시는 셀렉트 필터였지만 목록 상단 탭(칩)으로 옮겼다 — 정보 관리 화면들과
 * 같은 필터 패턴이라 화면 간 일관성이 낫다.
 * 작성/수정/삭제는 에너지공사(1,2)만 — 서버 규칙과 동일하게 버튼을 거른다.
 */

export const FAQ_TYPE_LABEL: Record<FaqType, string> = {
  principle: '원리와 구성',
  effect: '절감효과',
  how: '설치방법',
  manage: '사후관리',
  safe: '안정성',
};

const FAQ_TYPES = Object.keys(FAQ_TYPE_LABEL) as FaqType[];
const ENERGY_ROLES = [1, 2];
const SIZE = 30;

export function FaqManage({ role }: { role: number }) {
  const [type, setType] = useState<FaqType | ''>('');
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const canWrite = ENERGY_ROLES.includes(role);

  const list = useAsync(
    () =>
      api.listBoards('faq', {
        type: type || undefined,
        keyword: applied || undefined,
        page,
        size: SIZE,
      }),
    [type, applied, page],
  );

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          <button
            type="button"
            className={type === '' ? 'chip active' : 'chip'}
            onClick={() => {
              setType('');
              setPage(1);
            }}
          >
            전체
          </button>
          {FAQ_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={type === t ? 'chip active' : 'chip'}
              onClick={() => {
                setType(t);
                setPage(1);
              }}
            >
              {FAQ_TYPE_LABEL[t]}
            </button>
          ))}

          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(keyword.trim());
              setPage(1);
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="제목 또는 내용"
            />
            <button type="submit">검색</button>
          </form>
          {canWrite && (
            <button type="button" className="primary create" onClick={() => setCreateOpen(true)}>
              등록하기
            </button>
          )}
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>No</th>
                <th>질문 형식</th>
                <th>제목</th>
                <th>글쓴이</th>
                <th>등록일시</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    등록된 질문이 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((b) => (
                <tr
                  key={b.id}
                  className={b.id === selected ? 'selected' : ''}
                  onClick={() => setSelected(b.id)}
                >
                  <td>{b.id}</td>
                  <td>{FAQ_TYPE_LABEL[b.type as FaqType] ?? b.type ?? '-'}</td>
                  <td>{b.title}</td>
                  <td>{b.author_name ?? '-'}</td>
                  <td>{formatDateTime(b.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>

      <aside className="admin__aside">
        {selected === null ? (
          <p className="muted">질문을 선택하세요.</p>
        ) : (
          <FaqDetail
            key={selected}
            faqId={selected}
            canWrite={canWrite}
            onClose={() => setSelected(null)}
            onChanged={() => list.reload()}
            onDeleted={() => {
              setSelected(null);
              list.reload();
            }}
          />
        )}
      </aside>

      {createOpen && (
        <FaqFormModal
          onClose={() => setCreateOpen(false)}
          onSaved={(id) => {
            setCreateOpen(false);
            setSelected(id);
            list.reload();
          }}
        />
      )}
    </div>
  );
}

function FaqDetail({
  faqId,
  canWrite,
  onClose,
  onChanged,
  onDeleted,
}: {
  faqId: number;
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const detail = useAsync(() => api.getBoard('faq', faqId), [faqId]);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteBoard('faq', faqId);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '삭제하지 못했습니다.');
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  if (detail.loading) return <Spinner />;
  if (detail.error) return <ErrorBox message={detail.error} onRetry={detail.reload} />;
  const post = detail.data;
  if (!post) return null;

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>{post.title}</h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel">
        <div className="field">
          <span className="field__label">질문 형식</span>
          <span className="field__value">
            {FAQ_TYPE_LABEL[post.type as FaqType] ?? post.type ?? '-'}
          </span>
        </div>
        <div className="field">
          <span className="field__label">글쓴이</span>
          <span className="field__value">{post.author_name ?? '-'}</span>
        </div>
        <div className="field">
          <span className="field__label">등록일시</span>
          <span className="field__value">{formatDateTime(post.created_at)}</span>
        </div>
      </section>

      <h3 className="section">내용</h3>
      <section className="panel">
        <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{post.contents ?? '-'}</p>
      </section>

      {error && <p className="error">{error}</p>}

      {canWrite && (
        <div className="row form-actions">
          <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>
            삭제
          </button>
          <button type="button" className="primary" disabled={busy} onClick={() => setEditOpen(true)}>
            수정
          </button>
        </div>
      )}

      {editOpen && (
        <FaqFormModal
          post={post}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            detail.reload();
            onChanged();
          }}
        />
      )}

      {confirmDelete && (
        <Modal title="자주하는 질문 삭제" onClose={() => setConfirmDelete(false)}>
          <p>
            <strong>{post.title}</strong> 질문을 삭제합니다. 계속할까요?
          </p>
          <div className="row">
            <button type="button" onClick={() => setConfirmDelete(false)}>
              취소
            </button>
            <button type="button" className="primary" disabled={busy} onClick={() => void remove()}>
              삭제
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** 등록/수정 겸용 폼 — 레거시 faq/edit.blade.php 와 같은 3필드(형식/제목/내용). */
function FaqFormModal({
  post,
  onClose,
  onSaved,
}: {
  post?: BoardDetailOut;
  onClose: () => void;
  onSaved: (id: number) => void;
}) {
  const [faqType, setFaqType] = useState<FaqType | ''>((post?.type as FaqType | undefined) ?? '');
  const [title, setTitle] = useState(post?.title ?? '');
  const [contents, setContents] = useState(post?.contents ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (faqType === '' || !title.trim() || !contents.trim()) {
      setError('질문 형식 · 제목 · 내용을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = { title: title.trim(), contents, faq_type: faqType };
      const saved = post
        ? await api.updateBoard('faq', post.id, body)
        : await api.createBoard('faq', body);
      onSaved(saved.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={post ? '자주하는 질문 수정' : '자주하는 질문 등록'} onClose={onClose}>
      <label className="labeled">
        질문 형식 *
        <select value={faqType} onChange={(e) => setFaqType(e.target.value as FaqType | '')}>
          <option value="">선택</option>
          {FAQ_TYPES.map((t) => (
            <option key={t} value={t}>
              {FAQ_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </label>
      <label className="labeled">
        제목 *
        <input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="labeled">
        내용 *
        <textarea rows={8} value={contents} onChange={(e) => setContents(e.target.value)} />
      </label>

      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '저장 중…' : post ? '수정' : '등록'}
        </button>
      </div>
    </Modal>
  );
}
